import * as React from "react";
import {Paginator} from "primereact/paginator";

import {constantTable} from "../../../../helpers/constants";
import {docsPaginatorTemplate} from "../../../../components/synced/ui/docsPaginatorTemplate/docsPaginatorTemplate";
import {fromCreatedQueryParams, toCreatedQueryParams} from "../../helpers/createdDateRangeToQueryParams";
import {PagedList} from "../../../../types/dataQueries/base/PagedList";
import {ProjectDoc} from "../../models/ProjectDoc";
import {ProjectDocMobileCard} from "./ProjectDocMobileCard";
import {ProjectDocQuery} from "../../models/ProjectDocQuery";
import {ProjectDocsMobileFilterChips} from "./ProjectDocsMobileFilterChips";
import {ProjectDocsMobileSearch} from "./ProjectDocsMobileSearch";
import styles from "./ProjectDocsMobileList.module.css";

export interface ProjectDocsMobileListProps {
    data: PagedList<ProjectDoc>;
    isLoading: boolean;
    initialQuery: ProjectDocQuery;
    updateQuery: (q: ProjectDocQuery) => void;
    onSelect: (doc: ProjectDoc) => void;
}

interface MobileFilterState {
    first: number;
    rows: number;
    titleContains?: string;
    projectTitle?: string;
    docTypeId?: number;
    statusIds?: number[];
    createdRange?: [string, string] | null;
}

const fromQuery = (q: ProjectDocQuery): MobileFilterState => {
    const range = q.CreatedQueryParams ? fromCreatedQueryParams(q.CreatedQueryParams) : null;
    return {
        first: q.Skip ?? 0,
        rows: q.Take ?? constantTable.paginatorRows,
        titleContains: q.TitleContains || undefined,
        projectTitle: q.ProjectTitle || undefined,
        docTypeId: q.DocTypeId,
        statusIds: q.StatusIds?.length ? q.StatusIds : undefined,
        createdRange: range?.[0] && range?.[1] ? [range[0].toISOString(), range[1].toISOString()] : null,
    };
};

export const ProjectDocsMobileList: React.FC<ProjectDocsMobileListProps> = ({
                                                                                data,
                                                                                isLoading,
                                                                                initialQuery,
                                                                                updateQuery,
                                                                                onSelect
                                                                            }) => {
    const [state, setState] = React.useState<MobileFilterState>(() => fromQuery(initialQuery));
    const typingTimeoutRef = React.useRef(0);

    const toQuery = (s: MobileFilterState): ProjectDocQuery => ({
        TitleContains: s.titleContains ?? ``,
        ProjectTitle: s.projectTitle ?? ``,
        DocTypeId: s.docTypeId ? s.docTypeId : undefined,
        StatusIds: s.statusIds ?? [],
        CreatedQueryParams: s.createdRange
            ? toCreatedQueryParams([new Date(s.createdRange[0]), new Date(s.createdRange[1])])
            : undefined,
        Skip: s.first,
        Take: s.rows,
    });

    const applyState = (next: MobileFilterState) => {
        setState(next);
        updateQuery(toQuery(next));
    };

    const onSearchChange = (value: string) => {
        const next = {...state, titleContains: value, first: 0};
        setState(next);

        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = window.setTimeout(() => updateQuery(toQuery(next)), 1000);
    };

    const onProjectTitleChange = (value: string) => {
        const next = {...state, projectTitle: value, first: 0};
        setState(next);

        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = window.setTimeout(() => updateQuery(toQuery(next)), 1000);
    };



    const onDocTypeChange = (v: number) => applyState({...state, docTypeId: v, first: 0});
    const onStatusChange = (v: number[]) => applyState({...state, statusIds: v, first: 0});
    const onCreatedChange = (range: [Date, Date] | null) =>
        applyState({...state, createdRange: range ? [range[0].toISOString(), range[1].toISOString()] : null, first: 0});

    const onPageChange = (e: { first: number; rows: number }) => applyState({...state, first: e.first, rows: e.rows});

    const createdRangeAsDates = state.createdRange
        ? [new Date(state.createdRange[0]), new Date(state.createdRange[1])]
        : null;

    return (
        <div className={styles.container}>
            <div className={styles.heading}>
                <div className={styles.title}>Документация</div>
                <div className={styles.count}>{data.TotalCount} документов</div>
            </div>

            <ProjectDocsMobileSearch value={state.titleContains ?? ``} onChange={onSearchChange}/>
            <ProjectDocsMobileFilterChips
                projectTitleValue={state.projectTitle}
                onProjectTitleChange={onProjectTitleChange}
                docTypeId={state.docTypeId}
                statusIds={state.statusIds}
                createdRange={createdRangeAsDates}
                onDocTypeChange={onDocTypeChange}
                onStatusChange={onStatusChange}
                onCreatedChange={onCreatedChange}/>

            <div className={styles.cardList}>
                {data.Items.length === 0 && !isLoading && (
                    <div className={styles.emptyMessage}>{constantTable.emptyMessage}</div>
                )}
                {data.Items.map((doc) => (
                    <ProjectDocMobileCard key={doc.Id} doc={doc} onClick={() => onSelect(doc)}/>
                ))}
            </div>

            <Paginator
                first={state.first}
                rows={state.rows}
                totalRecords={data.TotalCount}
                template={docsPaginatorTemplate}
                onPageChange={onPageChange}/>
        </div>
    );
};
